// 대균열(Greater Rift, C12) 연출 — 그래픽·손맛 레인. 게임(game/field/rift.ts)이 내는 FieldEv.GriftEnter·RiftGuardian·GriftResult와
// view.field.rift(tier·phase·chest)를 읽기만 하고 그린다. 일반 균열(보라)과 구분되는 심홍(crimson)·청금(lapis) 대비, 단계가 오를수록 색 세기·안개 밀도가 조금씩 오른다.
//  - 입장: 영웅 발밑 소용돌이(입자가 안쪽으로 감겨 오름) + 충격 고리 두 겹(심홍 → 청금) + 작은 중심 광휘. 층 안에서는 가장자리(영웅에게서 6~15m) 불티가 천천히 오른다.
//  - 수호자: 바닥 룬 서클 두 겹(청금 바깥 + 심홍 안쪽, 반대로 돈다) + 수호자 발밑 오라(낮은 반투명 원통 + 가는 고리) + 둘레 불티.
//  - 결과: 시간 안 돌파 → 상자 위 빛기둥(흰금 심 + 청금 겉) + 솟는 불꽃 + 단계 숫자(상자 위 7m, 플레이 높이 밖), 이정표(d>0)면 기둥이 굵고 왕관 고리 + 두 번째 충격파.
//    시간 초과 → 잿빛 고리 하나와 느린 티끌(기둥·숫자·카메라 없음).
//  - 카메라: 돌파 순간 기존 액션 카메라 디렉터 컷 'roomClear'(짧게 다가가는 구도, 흔들림·줌 펄스 없음)를 상자에 건다.
// 규칙(AV-SPEC·RAID-VFX): 화면 전체 섬광 없음(모든 밝은 것은 그 자리 광휘), 밝은 시작(광휘·기둥 점화)은 FlashLimiter로 1초 3회·간격 334ms 이하,
// 섬광 줄이기 설정이면 가산 밝기 60%. 플레이 영역을 덮는 판 없음: 바닥 표식은 가산·낮은 불투명도, 가장자리 불티는 영웅 6m 밖에서만 난다.
// 예산: 입자 풀 192(품질 low 0.5배로 생성 수만 줄임) 드로우콜 1, 고리 4 · 룬 2 · 오라 2 · 기둥 3 · 숫자 1 = 최대 드로우콜 13(보이는 것만).
// 프레임 루프 할당 없음: 모든 배열·재질·메시는 생성자에서 만들고, 숫자 캔버스는 처음 돌파 때 한 번 만든 뒤 다시 그리기만 한다.
import * as THREE from 'three/webgpu';
import type { QualityLevel } from '../../shared/config';
import type { GameEvent } from '../../shared/events';
import { FieldEv, RiftPhase } from '../../shared/field/view';
import { BossState, Scene, type GameView } from '../../shared/types';
import { FlashLimiter } from '../../vfx/flash';
import type { ShotId } from '../cine';

/** 대균열 색: 심홍·청금 대비 + 흰금(돌파) + 잿빛(시간 초과) */
export const GRIFT_COLOR = { crimson: 0xff2a4e, lapis: 0x3f62ff, gold: 0xffd27a, pale: 0xfff2d2, ash: 0x8ea2bf, fog: 0x34123c } as const;
/** 입자 풀 상한·품질 배율 */
export const GRIFT_PARTICLE_CAP = 192;
const DENSITY: Record<QualityLevel, number> = { low: 0.5, mid: 0.75, high: 1 };
/** 입자 수 예산(high 기준, 한 번 생성) */
export const GRIFT_BURST = { vortex: 56, guardian: 18, clear: 32, milestone: 56, late: 12, ambientPerSec: 8, ambientPerSecMax: 18 } as const;
/** 밝은 시작 광휘의 길이(ms) — FlashLimiter 1회 100ms 이하 */
const FLARE_MS = 90;
/** 섬광 줄이기 설정의 가산 밝기 배율(RAID-VFX와 같은 값) */
export const GRIFT_REDUCE_FLASH = 0.6;
/** 빛기둥: 높이(m)·불투명도(돌파/이정표). 아래 18%는 투명(beamAlpha) */
export const GRIFT_BEAM = { height: 12, core: 0.5, coreBig: 0.62, outer: 0.18, outerBig: 0.24 } as const;
/** 카메라 수평 거리(m) → 기둥 밝기 배율: 6m 안 0.25, 14m 밖 1 */
export function griftBeamFade(d: number): number {
  const t = Math.min(1, Math.max(0, (d - 6) / 8));
  return 0.25 + 0.75 * t * t * (3 - 2 * t);
}
/** 단계 숫자 높이(m): 상자 위, 영웅·몹 키(2m) 위로 충분히 떨어진 하늘 쪽 */
export const GRIFT_NUMBER_Y = 7.2;
/** 가까운 카메라 구도에서 7.2m가 화면 위로 나가면 내려 줄 최저 높이(영웅·몹 키 2m 위) */
export const GRIFT_NUMBER_Y_MIN = 3.2;
/** 숫자 중심이 머물러야 할 화면 높이 상한(NDC y, 위 줄 HUD 아래) */
export const GRIFT_NUMBER_NDC_MAX = 0.55;
/** 숫자 높이: 7.2m와 최저 높이에서 투영한 NDC y로 선형 보간해, 화면 위 줄 HUD 아래(NDC_MAX)에 들어오는 가장 높은 높이 */
export function griftNumberHeight(ndcTop: number, ndcLow: number, max = GRIFT_NUMBER_NDC_MAX): number {
  if (!(ndcTop > max)) return GRIFT_NUMBER_Y;
  if (!(ndcLow < max) || !(ndcTop > ndcLow)) return GRIFT_NUMBER_Y_MIN;
  return GRIFT_NUMBER_Y_MIN + (GRIFT_NUMBER_Y - GRIFT_NUMBER_Y_MIN) * ((max - ndcLow) / (ndcTop - ndcLow));
}
/** 가장자리 불티 고리(영웅 기준 m): 안쪽 반경 안에는 나지 않는다(전투 시야) */
export const GRIFT_AMBIENT_RING = { inner: 6, outer: 15 } as const;

/** 입장 소용돌이는 층 로딩 멈춤이 끝나 프레임이 이 길이(초) 아래로 연속 GRIFT_SMOOTH_FRAMES번 돌 때 터진다. 늦어도 GRIFT_ENTER_WAIT_MS 뒤 */
export const GRIFT_SMOOTH_DT = 0.2;
export const GRIFT_SMOOTH_FRAMES = 2;
export const GRIFT_ENTER_WAIT_MS = 4000;
/** 단계 → 0..1 세기(1단계 0, 150단계 ≈ 0.96). 지수형이라 낮은 단계에서 빨리 오르고 높은 단계에서 천천히 포화된다 */
export function griftStrength(tier: number): number {
  if (!(tier > 0)) return 0;
  const t = Math.min(150, Math.max(1, Math.floor(tier)));
  return 1 - Math.exp(-(t - 1) / 45);
}
/** 단계 → 분위기: 안개 색을 대균열 색 쪽으로 섞는 비율, 안개 시작·끝 거리 배율(밀도가 조금씩 오른다), 색 세기 */
export function griftMood(tier: number): GriftMood {
  if (!(tier > 0)) return MOOD_NONE;
  // 단계가 바뀔 때만 새로 계산한다: 프레임 루프(안개·입자 갱신)가 같은 객체를 공유해 프레임당 할당 0. 반환 객체는 읽기 전용
  if (tier === moodTier) return moodCache;
  const s = griftStrength(tier);
  moodTier = tier;
  moodCache = Object.freeze({ mix: 0.3 + 0.22 * s, near: 1 - 0.3 * s, far: 1 - 0.2 * s, glow: 0.6 + 0.4 * s });
  return moodCache;
}
export interface GriftMood { readonly mix: number; readonly near: number; readonly far: number; readonly glow: number }
const MOOD_NONE: GriftMood = Object.freeze({ mix: 0, near: 1, far: 1, glow: 0 });
let moodTier = 0;
let moodCache: GriftMood = MOOD_NONE;
/** 결과 연출 크기: 0 없음 · 1 시간 초과(차분) · 2 돌파 · 3 이정표 돌파 */
export function griftResultLevel(onTime: number, milestoneStones: number): 0 | 1 | 2 | 3 {
  if (onTime !== 1) return onTime === 0 ? 1 : 0;
  return milestoneStones > 0 ? 3 : 2;
}

// ── 절차 텍스처(생성자에서 한 번) ─────────────────────────
function dataTex(w: number, h: number, fill: (x: number, y: number) => number): THREE.DataTexture {
  const d = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const a = Math.max(0, Math.min(1, fill(x, y)));
    const o = (y * w + x) * 4;
    d[o] = d[o + 1] = d[o + 2] = 255; d[o + 3] = Math.round(a * 255);
  }
  const t = new THREE.DataTexture(d, w, h, THREE.RGBAFormat);
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearFilter; t.needsUpdate = true;
  return t;
}
/** 둥근 불티: 밝은 심 + 부드러운 테 */
function dotTexture(): THREE.DataTexture {
  const N = 32;
  return dataTex(N, N, (x, y) => {
    const r = Math.hypot(x + 0.5 - N / 2, y + 0.5 - N / 2) / (N / 2);
    return r >= 1 ? 0 : Math.pow(1 - r, 1.6) + 0.5 * Math.exp(-r * r * 30);
  });
}
/** 세로 그라데이션(기둥·오라 원통): 아래 진하고 위로 사라진다. 양 끝 0 */
function columnTexture(): THREE.DataTexture {
  const H = 64;
  return dataTex(1, H, (_x, y) => { const v = y / (H - 1); return Math.pow(1 - v, 1.4) * Math.min(1, v * 14 + 0.15); });
}
/** 빛기둥 그라데이션: 아래 6%는 비우고 24%(약 2.9m, 영웅 머리 위)까지 투명에서 차오르고 위로 갈수록 사라진다 — 상자 곁에 선 영웅을 덮지 않는다 */
export function beamAlpha(v: number): number {
  const rise = Math.min(1, Math.max(0, (v - 0.06) / 0.18));
  return rise * rise * Math.pow(1 - v, 1.8);
}
function beamTexture(): THREE.DataTexture {
  const H = 64;
  return dataTex(1, H, (_x, y) => beamAlpha(y / (H - 1)));
}
/** 룬 서클: 바깥 이중 테 + 눈금 + 룬 글자 자리(짧은 획) 24개 + 안쪽 육각별 + 안쪽 테 */
export function runeAlpha(u: number, v: number): number {
  const x = u * 2 - 1, y = v * 2 - 1, r = Math.hypot(x, y);
  if (r > 1) return 0;
  const a = Math.atan2(y, x);
  const band = (c: number, w: number): number => Math.max(0, 1 - Math.abs(r - c) / w);
  let k = band(0.95, 0.025) + band(0.885, 0.012) * 0.8 + band(0.58, 0.016) * 0.85 + band(0.52, 0.008) * 0.6;
  // 눈금(72개) — 바깥 두 테 사이
  if (r > 0.895 && r < 0.94) { const t = ((a / (Math.PI * 2)) * 72) % 1; if (Math.abs(t < 0 ? t + 1 : t) < 0.12) k += 0.7; }
  // 룬 글자 자리: 24칸마다 세로 획 + 가로 획 하나(칸마다 높이가 다르다)
  if (r > 0.64 && r < 0.84) {
    const cell = ((a / (Math.PI * 2)) * 24 + 24) % 24, i = Math.floor(cell), f = cell - i;
    const rr = (r - 0.64) / 0.2, hh = 0.25 + ((i * 7) % 5) * 0.14;
    if (Math.abs(f - 0.5) < 0.06) k += 0.75;
    if (Math.abs(rr - hh) < 0.06 && Math.abs(f - 0.5) < 0.28) k += 0.65;
    if (i % 3 === 0 && Math.abs(rr - 0.85) < 0.05 && Math.abs(f - 0.5) < 0.18) k += 0.5;
  }
  // 육각별: 두 삼각형의 변(반경 0.5 안)
  if (r < 0.5) {
    for (let s = 0; s < 6; s++) {
      // 육각형 변(법선 60° 간격, 중심 거리 0.25)을 반경 0.5까지 늘이면 두 삼각형이 겹친 별이 된다
      const th = (s * Math.PI) / 3 + Math.PI / 6;
      const nx = Math.cos(th), ny = Math.sin(th);
      const d = Math.abs(x * nx + y * ny - 0.25);
      if (d < 0.012) k += 0.8;
    }
    k += band(0.12, 0.02) * 0.7;
  }
  return Math.min(1, k);
}
function runeTexture(): THREE.DataTexture {
  const N = 256;
  return dataTex(N, N, (x, y) => runeAlpha((x + 0.5) / N, (y + 0.5) / N));
}

// ── 입자 풀(극좌표: 중심 둘레를 돌며 반경·높이가 변한다) ─────────────
const F = { cx: 0, cz: 1, ang: 2, rad: 3, y: 4, dAng: 5, dRad: 6, dY: 7, age: 8, life: 9, size: 10, r: 11, g: 12, b: 13, gain: 14 } as const;
const STRIDE = 15;

export class GriftSparks {
  readonly mesh: THREE.InstancedMesh;
  readonly cap: number;
  private readonly p: Float32Array;
  count = 0;
  /** 상한에 막혀 버린 생성 수(QA) */
  dropped = 0;
  private readonly col = new THREE.Color();
  constructor(cap: number, tex: THREE.Texture) {
    this.cap = cap;
    this.p = new Float32Array(cap * STRIDE);
    const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false, side: THREE.DoubleSide });
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, this.col.setRGB(0, 0, 0));
    this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 6;
    this.mesh.count = 0;
    this.mesh.name = 'grift-sparks';
  }
  /** 입자 하나. 가득 차면 버린다(false) */
  spawn(cx: number, cz: number, ang: number, rad: number, y: number, dAng: number, dRad: number, dY: number, life: number, size: number, hex: number, gain: number): boolean {
    if (this.count >= this.cap) { this.dropped++; return false; }
    const o = this.count++ * STRIDE, p = this.p;
    this.col.setHex(hex);
    p[o + F.cx] = cx; p[o + F.cz] = cz; p[o + F.ang] = ang; p[o + F.rad] = rad; p[o + F.y] = y;
    p[o + F.dAng] = dAng; p[o + F.dRad] = dRad; p[o + F.dY] = dY; p[o + F.age] = 0; p[o + F.life] = Math.max(0.05, life);
    p[o + F.size] = size; p[o + F.r] = this.col.r; p[o + F.g] = this.col.g; p[o + F.b] = this.col.b; p[o + F.gain] = gain;
    return true;
  }
  /** 진행 + 빌보드 행렬. right/up = 카메라 기준 축(월드) */
  update(dt: number, rx: number, ry: number, rz: number, ux: number, uy: number, uz: number, bright: number): void {
    const p = this.p, m = this.mesh.instanceMatrix.array as Float32Array, c = this.mesh.instanceColor!.array as Float32Array;
    let i = 0;
    while (i < this.count) {
      const o = i * STRIDE;
      const age = p[o + F.age] + dt;
      if (age >= p[o + F.life]) {
        // 끝: 마지막 입자를 이 자리로(순서 상관없음)
        const last = (this.count - 1) * STRIDE;
        if (last !== o) p.copyWithin(o, last, last + STRIDE);
        this.count--;
        continue;
      }
      p[o + F.age] = age;
      p[o + F.ang] += p[o + F.dAng] * dt;
      p[o + F.rad] = Math.max(0, p[o + F.rad] + p[o + F.dRad] * dt);
      p[o + F.y] += p[o + F.dY] * dt;
      const t = age / p[o + F.life];
      const env = Math.min(1, t / 0.12) * (t > 0.55 ? 1 - (t - 0.55) / 0.45 : 1);
      const x = p[o + F.cx] + Math.cos(p[o + F.ang]) * p[o + F.rad], z = p[o + F.cz] + Math.sin(p[o + F.ang]) * p[o + F.rad], y = p[o + F.y];
      const s = p[o + F.size] * (0.7 + 0.3 * env);
      const k = i * 16;
      m[k] = rx * s; m[k + 1] = ry * s; m[k + 2] = rz * s; m[k + 3] = 0;
      m[k + 4] = ux * s; m[k + 5] = uy * s; m[k + 6] = uz * s; m[k + 7] = 0;
      m[k + 8] = 0; m[k + 9] = 0; m[k + 10] = s; m[k + 11] = 0;
      m[k + 12] = x; m[k + 13] = y; m[k + 14] = z; m[k + 15] = 1;
      const g = env * p[o + F.gain] * bright, q = i * 3;
      c[q] = p[o + F.r] * g; c[q + 1] = p[o + F.g] * g; c[q + 2] = p[o + F.b] * g;
      i++;
    }
    this.mesh.count = this.count;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
  }
  clear(): void { this.count = 0; this.mesh.count = 0; }
}

/** 바닥 충격 고리 하나(가산, 반경이 퍼지며 사라진다) */
interface Wave { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; t0: number; dur: number; r0: number; r1: number; peak: number; on: boolean }

export class GriftFx {
  readonly group = new THREE.Group();
  readonly sparks: GriftSparks;
  readonly flash = new FlashLimiter();
  /** 기존 액션 카메라 디렉터 컷(렌더 묶음이 rig.cue로 잇는다) */
  onCue: ((id: ShotId, x: number, z: number) => boolean) | null = null;
  /** QA: 지금 보이는 요소·마지막 결과 */
  readonly qa = { enterAt: -1, resultAt: -1, enters: 0, guardians: 0, results: 0, lastLevel: 0, cues: 0, flares: 0, flaresBlocked: 0 };
  private readonly owned: { dispose(): void }[] = [];
  private readonly waves: Wave[] = [];
  private readonly runeOuter: THREE.Mesh;
  private readonly runeInner: THREE.Mesh;
  private readonly runeOuterMat: THREE.MeshBasicMaterial;
  private readonly runeInnerMat: THREE.MeshBasicMaterial;
  private readonly aura: THREE.Mesh;
  private readonly auraMat: THREE.MeshBasicMaterial;
  private readonly auraRing: THREE.Mesh;
  private readonly auraRingMat: THREE.MeshBasicMaterial;
  private readonly pillarCore: THREE.Mesh;
  private readonly pillarOuter: THREE.Mesh;
  private readonly pillarCoreMat: THREE.MeshBasicMaterial;
  private readonly pillarOuterMat: THREE.MeshBasicMaterial;
  private readonly crown: THREE.Mesh;
  private readonly crownMat: THREE.MeshBasicMaterial;
  private number: THREE.Sprite | null = null;
  private numberCanvas: HTMLCanvasElement | null = null;
  private numberTex: THREE.CanvasTexture | null = null;
  private readonly density: number;
  private now = 0;
  private last = -1;
  private seed = 0x2f6e2b1;
  // 진행 상태(ms, -1 = 없음)
  private runeAt = -1;
  private runeX = 0;
  private runeZ = 0;
  private pillarAt = -1;
  private pillarLevel = 0;
  private pillarX = 0;
  private pillarZ = 0;
  private secondWaveAt = -1;
  private ambientDebt = 0;
  private emberDebt = 0;
  private reduceFlash = false;
  /** 미뤄 둔 입장(단계, 0 = 없음)·요청 시각·연속 매끈한 프레임 수 */
  private pendingTier = 0;
  private pendingAt = 0;
  private smooth = 0;
  // 안개(장면의 Fog를 단계 색·밀도로 섞는다. 기본값은 지도 분위기가 다시 쓰면 다시 읽는다)
  private fog: THREE.Fog | null = null;
  private readonly fogBase = new THREE.Color();
  private fogBaseNear = 0;
  private fogBaseFar = 0;
  private fogApplied = false;
  private fogHex = -1;
  private fogNear = 0;
  private fogFar = 0;
  private readonly fogTint = new THREE.Color(GRIFT_COLOR.fog);
  private readonly tmp = new THREE.Color();
  private readonly camRight = new THREE.Vector3(1, 0, 0);
  /** 단계 숫자 투영용(프레임 루프 할당 없음) */
  private readonly projTmp = new THREE.Vector3();
  private readonly camUp = new THREE.Vector3(0, 1, 0);

  constructor(quality: QualityLevel) {
    this.group.name = 'grift-fx';
    // QA 읽기 전용 훅(?qa 빌드 화면에서만): 캡처 스크립트가 연출이 실제로 터진 시각을 기준으로 찍는다
    if (typeof window !== 'undefined' && typeof location !== 'undefined' && /[?&]qa\b/.test(location.search)) (window as unknown as { __DH_GRIFT__?: unknown }).__DH_GRIFT__ = this.qa;
    this.density = DENSITY[quality];
    const dot = dotTexture(), col = columnTexture(), rune = runeTexture(), beam = beamTexture();
    this.owned.push(dot, col, rune, beam);
    this.sparks = new GriftSparks(GRIFT_PARTICLE_CAP, dot);
    this.owned.push(this.sparks.mesh.geometry, this.sparks.mesh.material as THREE.Material);
    this.group.add(this.sparks.mesh);
    const add = (mat: THREE.MeshBasicMaterial): THREE.MeshBasicMaterial => { mat.transparent = true; mat.depthWrite = false; mat.blending = THREE.AdditiveBlending; mat.fog = false; mat.side = THREE.DoubleSide; this.owned.push(mat); return mat; };
    // 충격 고리 4개(공용 기하)
    const ringGeo = new THREE.RingGeometry(0.88, 1, 72);
    ringGeo.rotateX(-Math.PI / 2);
    this.owned.push(ringGeo);
    for (let i = 0; i < 4; i++) {
      const mat = add(new THREE.MeshBasicMaterial({ color: GRIFT_COLOR.crimson, opacity: 0 }));
      const mesh = new THREE.Mesh(ringGeo, mat);
      mesh.renderOrder = 4; mesh.visible = false; mesh.frustumCulled = false;
      this.group.add(mesh);
      this.waves.push({ mesh, mat, t0: 0, dur: 1, r0: 0, r1: 1, peak: 0, on: false });
    }
    // 룬 서클 두 겹(바닥 y 0.04 — 경고 데칼 y 0.03 위, 가산이라 경고 색을 덮지 않고 더한다)
    const flat = new THREE.PlaneGeometry(1, 1);
    flat.rotateX(-Math.PI / 2);
    this.owned.push(flat);
    this.runeOuterMat = add(new THREE.MeshBasicMaterial({ map: rune, color: GRIFT_COLOR.lapis, opacity: 0 }));
    this.runeInnerMat = add(new THREE.MeshBasicMaterial({ map: rune, color: GRIFT_COLOR.crimson, opacity: 0 }));
    this.runeOuter = new THREE.Mesh(flat, this.runeOuterMat);
    this.runeInner = new THREE.Mesh(flat, this.runeInnerMat);
    this.runeOuter.position.y = 0.04; this.runeInner.position.y = 0.05;
    this.runeOuter.renderOrder = 3; this.runeInner.renderOrder = 3;
    // 수호자 오라: 낮은 원통(위로 사라지는 그라데이션) + 발밑 가는 고리
    const cyl = new THREE.CylinderGeometry(1, 1.12, 1, 40, 1, true);
    cyl.translate(0, 0.5, 0);
    this.owned.push(cyl);
    this.auraMat = add(new THREE.MeshBasicMaterial({ map: col, color: GRIFT_COLOR.crimson, opacity: 0 }));
    this.aura = new THREE.Mesh(cyl, this.auraMat);
    this.auraRingMat = add(new THREE.MeshBasicMaterial({ color: GRIFT_COLOR.lapis, opacity: 0 }));
    this.auraRing = new THREE.Mesh(ringGeo, this.auraRingMat);
    this.auraRing.position.y = 0.06;
    // 빛기둥: 흰금 심 + 청금 겉(위로 사라짐) + 이정표 왕관 고리(공중의 가로 고리)
    this.pillarCoreMat = add(new THREE.MeshBasicMaterial({ map: beam, color: GRIFT_COLOR.pale, opacity: 0 }));
    this.pillarOuterMat = add(new THREE.MeshBasicMaterial({ map: beam, color: GRIFT_COLOR.lapis, opacity: 0 }));
    // 양면 가산은 겹쳐 두 배로 밝아진다: 기둥은 앞면만
    this.pillarCoreMat.side = THREE.FrontSide; this.pillarOuterMat.side = THREE.FrontSide;
    this.pillarCore = new THREE.Mesh(cyl, this.pillarCoreMat);
    this.pillarOuter = new THREE.Mesh(cyl, this.pillarOuterMat);
    this.crownMat = add(new THREE.MeshBasicMaterial({ color: GRIFT_COLOR.gold, opacity: 0 }));
    this.crown = new THREE.Mesh(ringGeo, this.crownMat);
    for (const m of [this.runeOuter, this.runeInner, this.aura, this.auraRing, this.pillarCore, this.pillarOuter, this.crown]) { m.visible = false; m.frustumCulled = false; this.group.add(m); }
    this.aura.renderOrder = 5; this.pillarCore.renderOrder = 6; this.pillarOuter.renderOrder = 5; this.crown.renderOrder = 6;
  }

  /** 렌더 묶음이 장면 안개를 넘긴다(대균열 층 분위기) */
  bindFog(fog: THREE.Fog | null): void { this.fog = fog; }

  private rand(): number {
    let s = this.seed;
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    this.seed = s;
    return (s >>> 0) / 4294967296;
  }
  private n(base: number): number { return Math.max(1, Math.round(base * this.density)); }
  private gain(): number { return this.reduceFlash ? GRIFT_REDUCE_FLASH : 1; }

  /** 밝은 시작 하나를 섬광 예산에 묻는다(허락되지 않으면 그 광휘는 절반 세기로) */
  private flare(): number {
    if (this.flash.request(this.now, FLARE_MS, 1)) { this.qa.flares++; return 1; }
    this.qa.flaresBlocked++;
    return 0.5;
  }

  private wave(x: number, z: number, hex: number, r0: number, r1: number, dur: number, peak: number, y = 0.06): void {
    let w = this.waves[0];
    for (const c of this.waves) { if (!c.on) { w = c; break; } if (c.t0 < w.t0) w = c; }
    w.on = true; w.t0 = this.now; w.dur = dur * 1000; w.r0 = r0; w.r1 = r1; w.peak = peak;
    w.mat.color.setHex(hex);
    w.mesh.position.set(x, y, z);
    w.mesh.visible = true;
  }

  /** 게임 이벤트(렌더 묶음의 onEvent가 넘긴다). 대균열이 아닌 판의 이벤트는 무시한다 */
  onEvent(e: GameEvent, view: GameView): void {
    const r = view.field?.rift;
    if (!r) return;
    // 입장은 층 지도를 짓는 긴 프레임과 같은 틱에 온다: 바로 터뜨리면 로딩 멈춤 동안 다 지나가 보이지 않는다(2026-11-01 캡처).
    // 매끈한 프레임이 이어질 때(또는 늦어도 GRIFT_ENTER_WAIT_MS 뒤) update가 그때의 영웅 자리에서 터뜨린다
    if (e.type === FieldEv.GriftEnter) { this.pendingTier = Math.max(1, e.a | 0); this.pendingAt = this.now; this.smooth = 0; }
    else if (e.type === FieldEv.RiftGuardian && r.tier > 0) this.guardian(e.x, e.z, r.tier);
    else if (e.type === FieldEv.GriftResult) this.result(r.chestX, r.chestZ, e.a, e.c, e.d);
  }

  /** 입장 소용돌이: 충격 고리 둘 + 안쪽으로 감겨 오르는 입자 + 중심 광휘 */
  enter(x: number, z: number, tier: number): void {
    this.qa.enters++; this.qa.enterAt = this.now;
    const mood = griftMood(tier), g = this.gain(), f = this.flare();
    this.wave(x, z, GRIFT_COLOR.crimson, 0.6, 10, 0.75, 0.85 * g);
    this.wave(x, z, GRIFT_COLOR.lapis, 0.3, 7, 0.9, 0.7 * g);
    this.wave(x, z, GRIFT_COLOR.pale, 0.1, 1.8, 0.32, 0.9 * g * f, 0.1);
    const n = this.n(GRIFT_BURST.vortex);
    for (let i = 0; i < n; i++) {
      const life = 0.9 + this.rand() * 0.35, rad = 4.8 + this.rand() * 3.4;
      const hex = i % 3 === 0 ? GRIFT_COLOR.lapis : i % 3 === 1 ? GRIFT_COLOR.crimson : GRIFT_COLOR.gold;
      this.sparks.spawn(x, z, this.rand() * Math.PI * 2, rad, 0.15 + this.rand() * 0.4, (3.2 + this.rand() * 1.4) * (1 + 0.3 * mood.glow), -(rad - 0.35) / life, 1.4 + this.rand() * 1.1, life, 0.26 + this.rand() * 0.2, hex, 1.1 * mood.glow);
    }
  }

  /** 수호자 등장: 바닥 룬 서클 두 겹 + (update가 따라가는) 오라 + 둘레 불티 */
  guardian(x: number, z: number, tier: number): void {
    this.qa.guardians++;
    this.runeAt = this.now; this.runeX = x; this.runeZ = z;
    const g = this.gain(), f = this.flare(), mood = griftMood(tier);
    this.wave(x, z, GRIFT_COLOR.lapis, 1, 6.5, 0.6, 0.75 * g * f);
    const n = this.n(GRIFT_BURST.guardian);
    for (let i = 0; i < n; i++) this.sparks.spawn(x, z, (i / n) * Math.PI * 2, 4.4, 0.1, -0.9, -0.6, 1.6 + this.rand() * 1.2, 1.2 + this.rand() * 0.5, 0.3, i % 2 ? GRIFT_COLOR.lapis : GRIFT_COLOR.crimson, mood.glow);
  }

  /** 결과: 돌파(2)·이정표(3) = 빛기둥·숫자·카메라 컷, 시간 초과(1) = 잿빛 고리와 티끌 */
  result(x: number, z: number, tier: number, onTime: number, milestone: number): void {
    const lv = griftResultLevel(onTime, milestone);
    this.qa.results++; this.qa.lastLevel = lv; this.qa.resultAt = this.now;
    this.runeAt = -1;
    if (lv === 0) return;
    const g = this.gain();
    if (lv === 1) {
      this.wave(x, z, GRIFT_COLOR.ash, 0.8, 4.5, 1.4, 0.45 * g);
      const n = this.n(GRIFT_BURST.late);
      for (let i = 0; i < n; i++) this.sparks.spawn(x, z, this.rand() * Math.PI * 2, 0.6 + this.rand() * 1.4, 0.6 + this.rand() * 0.8, 0.25, 0.15, 0.25 + this.rand() * 0.2, 2.4 + this.rand() * 0.8, 0.18, GRIFT_COLOR.ash, 0.5);
      return;
    }
    const big = lv === 3, f = this.flare();
    this.pillarAt = this.now; this.pillarLevel = lv; this.pillarX = x; this.pillarZ = z;
    this.wave(x, z, GRIFT_COLOR.gold, 0.6, big ? 9 : 6.5, 0.7, 0.9 * g * f);
    this.secondWaveAt = big ? this.now + 350 : -1;
    const n = this.n(big ? GRIFT_BURST.milestone : GRIFT_BURST.clear);
    for (let i = 0; i < n; i++) {
      const hex = i % 4 === 0 ? GRIFT_COLOR.lapis : i % 4 === 1 ? GRIFT_COLOR.crimson : GRIFT_COLOR.gold;
      this.sparks.spawn(x, z, this.rand() * Math.PI * 2, 0.25 + this.rand() * (big ? 1.2 : 0.8), 0.4 + this.rand() * 0.6, 1.2 + this.rand() * 1.2, 0.35, 3.2 + this.rand() * (big ? 4.5 : 3), 1.1 + this.rand() * 0.6, 0.22 + this.rand() * 0.16, hex, 1.15);
    }
    this.showNumber(tier, big);
    if (this.onCue && this.onCue('roomClear', x, z)) this.qa.cues++;
  }

  /** 단계 숫자 스프라이트(문서가 없는 시험 환경에서는 건너뛴다). 캔버스는 처음 한 번 만들고 다시 그리기만 한다 */
  private showNumber(tier: number, big: boolean): void {
    if (typeof document === 'undefined') return;
    if (!this.numberCanvas) {
      const c = document.createElement('canvas');
      c.width = 512; c.height = 224;
      this.numberCanvas = c;
      this.numberTex = new THREE.CanvasTexture(c);
      this.numberTex.colorSpace = THREE.SRGBColorSpace;
      const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.numberTex, transparent: true, depthWrite: false, fog: false, opacity: 0 }));
      sp.scale.set(4.4, 4.4 * (224 / 512), 1);
      sp.renderOrder = 12;
      sp.visible = false;
      this.number = sp;
      this.group.add(sp);
      this.owned.push(this.numberTex, sp.material);
    }
    const g = this.numberCanvas.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, 512, 224);
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.font = '900 132px system-ui, "Malgun Gothic", sans-serif';
    g.lineJoin = 'round';
    g.lineWidth = 16; g.strokeStyle = 'rgba(30,40,120,0.9)';
    g.strokeText(String(tier), 256, 92);
    g.lineWidth = 5; g.strokeStyle = big ? '#ff6a86' : '#8fa6ff';
    g.strokeText(String(tier), 256, 92);
    g.fillStyle = big ? '#ffe7a8' : '#ffd27a';
    g.fillText(String(tier), 256, 92);
    g.font = '800 40px system-ui, "Malgun Gothic", sans-serif';
    g.lineWidth = 8; g.strokeStyle = 'rgba(20,24,60,0.9)';
    const sub = big ? '단계 돌파 · 이정표' : '단계 돌파';
    g.strokeText(sub, 256, 190);
    g.fillStyle = '#fff2d2';
    g.fillText(sub, 256, 190);
    this.numberTex!.needsUpdate = true;
  }

  /** 매 프레임. nowMs = 실시간(ms). 장면이 균열이 아니면 모든 것을 끈다 */
  update(view: GameView, nowMs: number, camera: THREE.Camera | null): void {
    this.now = nowMs;
    // 안정화 판정은 제한 전 실제 프레임 간격으로, 입자 적분용 dt만 0.1초로 제한한다
    const rawDt = this.last < 0 ? 0 : Math.max(0, (nowMs - this.last) / 1000), dt = Math.min(0.1, rawDt);
    this.last = nowMs;
    this.reduceFlash = view.settings.reduceFlash;
    this.smooth = rawDt > 0 && rawDt < GRIFT_SMOOTH_DT ? this.smooth + 1 : 0;
    const r = view.field?.rift, inRift = view.flow.scene === Scene.Rift && !!r;
    if (this.pendingTier > 0 && inRift && (this.smooth >= GRIFT_SMOOTH_FRAMES || nowMs - this.pendingAt >= GRIFT_ENTER_WAIT_MS)) {
      const t = this.pendingTier;
      this.pendingTier = 0;
      this.enter(view.hero.x, view.hero.z, t);
    }
    const tier = inRift && r ? r.tier : 0;
    this.applyFog(tier);
    this.group.visible = inRift;
    if (!inRift || !r) { if (nowMs - this.pendingAt > GRIFT_ENTER_WAIT_MS) this.pendingTier = 0; this.sparks.clear(); this.runeAt = -1; this.pillarAt = -1; this.secondWaveAt = -1; for (const w of this.waves) { w.on = false; w.mesh.visible = false; } this.hideAll(); return; }
    const mood = griftMood(tier), g = this.gain();
    // 가장자리 불티(대균열 층 분위기): 영웅 6~15m 고리에서만 나고 천천히 오른다
    if (tier > 0 && r.phase !== RiftPhase.None) {
      const rate = (GRIFT_BURST.ambientPerSec + (GRIFT_BURST.ambientPerSecMax - GRIFT_BURST.ambientPerSec) * griftStrength(tier)) * this.density;
      this.ambientDebt += rate * dt;
      while (this.ambientDebt >= 1) {
        this.ambientDebt -= 1;
        const rad = GRIFT_AMBIENT_RING.inner + this.rand() * (GRIFT_AMBIENT_RING.outer - GRIFT_AMBIENT_RING.inner);
        this.sparks.spawn(view.hero.x, view.hero.z, this.rand() * Math.PI * 2, rad, 0.1 + this.rand() * 0.6, (this.rand() - 0.5) * 0.25, 0, 0.35 + this.rand() * 0.4, 3 + this.rand() * 1.5, 0.2 + this.rand() * 0.14, this.rand() < 0.5 ? GRIFT_COLOR.crimson : GRIFT_COLOR.lapis, 0.75 * mood.glow);
      }
    } else this.ambientDebt = 0;
    // 충격 고리
    for (const w of this.waves) {
      if (!w.on) continue;
      const t = (nowMs - w.t0) / w.dur;
      if (t >= 1) { w.on = false; w.mesh.visible = false; continue; }
      const e = 1 - Math.pow(1 - t, 3), rr = w.r0 + (w.r1 - w.r0) * e;
      w.mesh.scale.set(rr, 1, rr);
      w.mat.opacity = w.peak * Math.pow(1 - t, 1.5);
    }
    if (this.secondWaveAt >= 0 && nowMs >= this.secondWaveAt) {
      this.secondWaveAt = -1;
      this.wave(this.pillarX, this.pillarZ, GRIFT_COLOR.lapis, 0.4, 11, 0.85, 0.8 * g * this.flare());
    }
    // 룬 서클(수호자 단계 동안, 등장 6초 뒤 0.8초에 걸쳐 사라진다) + 오라(수호자를 따라간다)
    const b = view.boss, guard = r.phase === RiftPhase.Guardian && tier > 0;
    if (this.runeAt >= 0 && guard) {
      const age = (nowMs - this.runeAt) / 1000;
      const inK = Math.min(1, age / 0.45), outK = age > 6 ? Math.max(0, 1 - (age - 6) / 0.8) : 1, k = inK * outK;
      const s = 9 * (0.6 + 0.4 * (1 - Math.pow(1 - inK, 3)));
      this.runeOuter.visible = this.runeInner.visible = k > 0;
      this.runeOuter.position.set(this.runeX, 0.04, this.runeZ); this.runeInner.position.set(this.runeX, 0.05, this.runeZ);
      this.runeOuter.scale.set(s, 1, s); this.runeInner.scale.set(s * 0.56, 1, s * 0.56);
      this.runeOuter.rotation.y = age * 0.35; this.runeInner.rotation.y = -age * 0.6;
      this.runeOuterMat.opacity = 0.7 * k * g * mood.glow; this.runeInnerMat.opacity = 0.55 * k * g * mood.glow;
      if (k <= 0) this.runeAt = -1;
    } else { this.runeOuter.visible = this.runeInner.visible = false; if (!guard) this.runeAt = -1; }
    const auraOn = guard && b.state !== BossState.Inactive && b.state !== BossState.Dead && b.state !== BossState.Dying;
    this.aura.visible = this.auraRing.visible = auraOn;
    if (auraOn) {
      const t = nowMs / 1000, rad = Math.max(1.6, b.radius * 1.25);
      this.aura.position.set(b.x, 0.02, b.z); this.aura.scale.set(rad, 2.4 + 0.15 * Math.sin(t * 2.2), rad);
      this.auraMat.opacity = (0.16 + 0.04 * Math.sin(t * 3.1)) * g * mood.glow;
      this.auraRing.position.set(b.x, 0.06, b.z); this.auraRing.scale.set(rad * 1.08, 1, rad * 1.08);
      this.auraRingMat.opacity = 0.5 * g * mood.glow;
      this.emberDebt += 6 * this.density * dt;
      while (this.emberDebt >= 1) { this.emberDebt -= 1; this.sparks.spawn(b.x, b.z, this.rand() * Math.PI * 2, rad * (0.9 + this.rand() * 0.25), 0.2, 0.6, 0, 1.2 + this.rand() * 0.9, 0.9 + this.rand() * 0.5, 0.2, this.rand() < 0.6 ? GRIFT_COLOR.crimson : GRIFT_COLOR.lapis, 0.8 * mood.glow); }
    } else this.emberDebt = 0;
    // 빛기둥(점화 0.25초 → 2.8초까지 숨쉬기 → 4초까지 사라짐) + 숫자(0.3초 떠오름 → 3.3초 → 4.1초 사라짐)
    if (this.pillarAt >= 0) {
      const age = (nowMs - this.pillarAt) / 1000, big = this.pillarLevel === 3;
      const rise = Math.min(1, age / 0.25), fade = age > 2.8 ? Math.max(0, 1 - (age - 2.8) / 1.2) : 1;
      const breathe = 1 + 0.06 * Math.sin(age * 5);
      const w = big ? 1.35 : 1, h = GRIFT_BEAM.height * (1 - Math.pow(1 - rise, 2));
      this.pillarCore.visible = this.pillarOuter.visible = fade > 0;
      this.pillarCore.position.set(this.pillarX, 0, this.pillarZ); this.pillarOuter.position.set(this.pillarX, 0, this.pillarZ);
      this.pillarCore.scale.set(0.5 * w * breathe, Math.max(0.01, h), 0.5 * w * breathe);
      this.pillarOuter.scale.set(1.25 * w, Math.max(0.01, h * 0.85), 1.25 * w);
      // 카메라가 기둥 축에 가까우면(돌파 컷이 상자로 다가갈 때) 기둥이 화면을 덮지 않게 흐린다(2026-11-01 캡처: 흰 기둥이 화면 절반을 덮음)
      const near = camera ? griftBeamFade(Math.hypot(camera.position.x - this.pillarX, camera.position.z - this.pillarZ)) : 1;
      this.pillarCoreMat.opacity = (big ? GRIFT_BEAM.coreBig : GRIFT_BEAM.core) * fade * g * near;
      this.pillarOuterMat.opacity = (big ? GRIFT_BEAM.outerBig : GRIFT_BEAM.outer) * fade * g * near;
      this.crown.visible = big && fade > 0 && age > 0.2;
      if (this.crown.visible) {
        const ck = Math.min(1, (age - 0.2) / 0.4);
        this.crown.position.set(this.pillarX, 5.4 + 0.3 * Math.sin(age * 2), this.pillarZ);
        this.crown.scale.set(1.9 + 0.5 * ck, 1, 1.9 + 0.5 * ck);
        this.crown.rotation.y = age * 0.8;
        this.crownMat.opacity = 0.75 * ck * fade * g;
      }
      const sp = this.number;
      if (sp) {
        const nk = Math.min(1, age / 0.3), nf = age > 3.3 ? Math.max(0, 1 - (age - 3.3) / 0.8) : 1;
        sp.visible = nk * nf > 0;
        let ny = GRIFT_NUMBER_Y;
        if (camera) {
          const v = this.projTmp;
          const top = v.set(this.pillarX, GRIFT_NUMBER_Y, this.pillarZ).project(camera).y;
          const low = v.set(this.pillarX, GRIFT_NUMBER_Y_MIN, this.pillarZ).project(camera).y;
          ny = griftNumberHeight(top, low);
        }
        sp.position.set(this.pillarX, ny - 0.6 * (1 - nk), this.pillarZ);
        (sp.material as THREE.SpriteMaterial).opacity = nk * nf;
      }
      if (fade <= 0 && age > 4.1) this.pillarAt = -1;
    } else { this.pillarCore.visible = this.pillarOuter.visible = this.crown.visible = false; if (this.number) this.number.visible = false; }
    // 입자: 카메라 기준 빌보드
    if (camera) {
      const e = camera.matrixWorld.elements;
      this.camRight.set(e[0], e[1], e[2]).normalize();
      this.camUp.set(e[4], e[5], e[6]).normalize();
    }
    const cr = this.camRight, cu = this.camUp;
    this.sparks.update(dt, cr.x, cr.y, cr.z, cu.x, cu.y, cu.z, g);
  }

  private hideAll(): void {
    this.runeOuter.visible = this.runeInner.visible = this.aura.visible = this.auraRing.visible = false;
    this.pillarCore.visible = this.pillarOuter.visible = this.crown.visible = false;
    if (this.number) this.number.visible = false;
  }

  /** 대균열 층이면 안개를 단계 색·밀도로 섞고, 아니면 기본값으로 되돌린다. 지도 분위기(applyMood)가 안개를 다시 쓰면 그 값을 새 기본으로 읽는다 */
  private applyFog(tier: number): void {
    const f = this.fog;
    if (!f) return;
    const touched = this.fogApplied && (f.color.getHex() !== this.fogHex || Math.abs(f.near - this.fogNear) > 1e-3 || Math.abs(f.far - this.fogFar) > 1e-3);
    if (touched) this.fogApplied = false; // 분위기가 다시 썼다: 지금 값이 새 기본
    if (tier <= 0) {
      if (this.fogApplied) { f.color.copy(this.fogBase); f.near = this.fogBaseNear; f.far = this.fogBaseFar; this.fogApplied = false; }
      return;
    }
    if (!this.fogApplied) { this.fogBase.copy(f.color); this.fogBaseNear = f.near; this.fogBaseFar = f.far; }
    const m = griftMood(tier);
    this.tmp.copy(this.fogBase).lerp(this.fogTint, m.mix);
    f.color.copy(this.tmp);
    f.near = this.fogBaseNear * m.near; f.far = Math.max(f.near + 10, this.fogBaseFar * m.far);
    this.fogHex = f.color.getHex(); this.fogNear = f.near; this.fogFar = f.far;
    this.fogApplied = true;
  }

  dispose(): void {
    for (const o of this.owned) o.dispose();
    this.owned.length = 0;
  }
}
